/**
 * ShellLayout
 *
 * The main app layout driven by AppConfig.
 * Renders a sidebar + content area using the design system Sidebar component.
 */

'use client';

import { LogOut, Moon, Search, Sun, Menu, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AvatarGroup,
  Button,
  CommandPalette,
  Inline,
  Sidebar,
  Typography,
  useCommandPalette,
  useTheme,
} from '@org/design-system';
import type { CommandItem, SidebarItem } from '@org/design-system';
import type { AppConfig, EntityConfig, SidebarNavConfig } from '../types/app-config';
import { resolveIcon } from '../types/icon-map';
import { useRuntimeAuth } from '../auth/auth-context';
import { isAuthEnabled } from '../auth/auth-paths';
import { resolvePermissionForAction } from '../auth/usePermission';
import { usePresence } from '../realtime/realtime-context';
import { usePlugin, usePluginNames } from '../plugins/plugin-registry';
import { useLocale, useTranslator, type TranslateFn } from '../i18n/locale-context';

// =============================================================================
// Sidebar Item Builder
// =============================================================================

const RECENT_ITEMS_STORAGE_KEY = 'runtime-shell:recent-items';
const MAX_RECENT_ITEMS = 8;

interface RecentItem {
  id: string;
  label: string;
  href: string;
  timestamp: number;
}

function readRecentItems(): RecentItem[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(RECENT_ITEMS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item): item is RecentItem => {
        if (!item || typeof item !== 'object') return false;
        const record = item as Record<string, unknown>;
        return (
          typeof record.id === 'string'
          && typeof record.label === 'string'
          && typeof record.href === 'string'
          && typeof record.timestamp === 'number'
        );
      })
      .slice(0, MAX_RECENT_ITEMS);
  } catch {
    return [];
  }
}

function writeRecentItems(items: RecentItem[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(RECENT_ITEMS_STORAGE_KEY, JSON.stringify(items));
}

function flattenSidebarNav(
  navItems: SidebarNavConfig[],
): SidebarNavConfig[] {
  const flattened: SidebarNavConfig[] = [];
  navItems.forEach(item => {
    flattened.push(item);
    if (item.children?.length) {
      flattened.push(...flattenSidebarNav(item.children));
    }
  });
  return flattened;
}

function findEntityByPath(
  href: string,
  entities: EntityConfig[],
): EntityConfig | undefined {
  const normalized = href.replace(/^\//, '').split('/')[0];
  return entities.find(entity => entity.slug === normalized);
}

function inferSidebarPermission(
  item: SidebarNavConfig,
  entities: EntityConfig[],
): string | undefined {
  if (item.requiredPermission) return item.requiredPermission;
  const matchedEntity = findEntityByPath(item.href, entities);
  if (!matchedEntity) return undefined;
  return resolvePermissionForAction('list', matchedEntity);
}

function resolvePathLabel(
  pathname: string,
  config: AppConfig,
  flattenedSidebar: SidebarNavConfig[],
  t: TranslateFn,
): string {
  const directMatch = flattenedSidebar.find(item => pathname === item.href);
  if (directMatch) return directMatch.label;

  const sidebarPrefixMatch = flattenedSidebar.find(item => (
    item.href !== '/' && pathname.startsWith(`${item.href}/`)
  ));
  if (sidebarPrefixMatch) return sidebarPrefixMatch.label;

  const customPage = config.pages.find(page => page.path === pathname);
  if (customPage) return customPage.title;

  for (const entity of config.entities) {
    if (pathname === `/${entity.slug}`) return entity.pluralName;
    if (pathname === `/${entity.slug}/new`) {
      return t('path.createEntity', { entityName: entity.name });
    }

    const detailMatcher = new RegExp(`^/${entity.slug}/([^/]+)$`);
    const editMatcher = new RegExp(`^/${entity.slug}/([^/]+)/edit$`);
    const detailMatch = pathname.match(detailMatcher);
    if (detailMatch) {
      return t('path.entityDetails', { entityName: entity.name, id: detailMatch[1] });
    }

    const editMatch = pathname.match(editMatcher);
    if (editMatch) {
      return t('path.editEntity', { entityName: entity.name });
    }
  }

  if (pathname === '/' || pathname === '') return t('common.home');
  return pathname.replace(/^\//, '');
}

function buildSidebarItems(
  navItems: SidebarNavConfig[],
  entities: EntityConfig[],
  pathname: string,
  navigate: (path: string) => void,
  onNavigate?: () => void,
): SidebarItem[] {
  return navItems.map(item => ({
    id: item.href,
    label: item.label,
    icon: item.icon ? resolveIcon(item.icon) : undefined,
    onClick: () => {
      navigate(item.href);
      onNavigate?.();
    },
    isActive:
      pathname === item.href ||
      (item.href !== '/' && pathname.startsWith(item.href + '/')),
    badge: item.badge,
    requiredPermission: inferSidebarPermission(item, entities),
    items: item.children
      ? buildSidebarItems(item.children, entities, pathname, navigate, onNavigate)
      : undefined,
  }));
}

export interface SidebarWidgetPluginProps {
  name: string;
  config: AppConfig;
  pathname: string;
  navigate: (path: string) => void;
}

type SidebarWidgetPluginComponent = React.ComponentType<SidebarWidgetPluginProps>;

function SidebarPluginWidget({
  widgetName,
  config,
  pathname,
  navigate,
}: {
  widgetName: string;
  config: AppConfig;
  pathname: string;
  navigate: (path: string) => void;
}) {
  const { plugin: SidebarWidget } = usePlugin<SidebarWidgetPluginComponent>(
    'sidebarWidgets',
    widgetName,
  );

  if (!SidebarWidget) {
    return null;
  }

  return (
    <SidebarWidget
      name={widgetName}
      config={config}
      pathname={pathname}
      navigate={navigate}
    />
  );
}

// =============================================================================
// Layout Component
// =============================================================================

interface ShellLayoutProps {
  config: AppConfig;
}

export function ShellLayout({ config }: ShellLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslator();
  const { locale, setLocale, supportedLocales } = useLocale();
  const { hasPermission, isAuthenticated, logout } = useRuntimeAuth();
  const presenceUsers = usePresence();
  const { theme, setTheme } = useTheme();
  const { getProps, setOpen: setCommandPaletteOpen } = useCommandPalette({
    shortcut: { key: 'k', metaKey: true, ctrlKey: true },
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [recentItems, setRecentItems] = useState<RecentItem[]>(() => readRecentItems());
  const sidebarWidgetNames = usePluginNames('sidebarWidgets');
  const flattenedSidebar = useMemo(
    () => flattenSidebarNav(config.sidebar),
    [config.sidebar],
  );

  const currentPathLabel = useMemo(
    () => resolvePathLabel(location.pathname, config, flattenedSidebar, t),
    [location.pathname, config, flattenedSidebar, t],
  );

  useEffect(() => {
    if (!location.pathname || location.pathname === '/login' || location.pathname === '/logout') {
      return;
    }

    const nextItem: RecentItem = {
      id: location.pathname,
      href: location.pathname,
      label: currentPathLabel,
      timestamp: Date.now(),
    };

    setRecentItems(prev => {
      const deduped = [nextItem, ...prev.filter(item => item.href !== nextItem.href)]
        .slice(0, MAX_RECENT_ITEMS);
      writeRecentItems(deduped);
      return deduped;
    });
  }, [location.pathname, currentPathLabel]);

  const sidebarItems = buildSidebarItems(
    config.sidebar,
    config.entities,
    location.pathname,
    navigate,
    () => setMobileSidebarOpen(false),
  );
  const sidebarFooter = sidebarWidgetNames.length > 0 ? (
    <div className="space-y-2">
      {sidebarWidgetNames.map(widgetName => (
        <SidebarPluginWidget
          key={widgetName}
          widgetName={widgetName}
          config={config}
          pathname={location.pathname}
          navigate={navigate}
        />
      ))}
    </div>
  ) : undefined;

  const commandItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [];

    flattenedSidebar.forEach(item => {
      const requiredPermission = inferSidebarPermission(item, config.entities);
      if (requiredPermission && !hasPermission(requiredPermission)) return;

      items.push({
        id: `nav:${item.href}`,
        label: item.label,
        description: item.href,
        group: 'Navigation',
        icon: item.icon ? resolveIcon(item.icon) : undefined,
        keywords: [item.href],
        onSelect: () => navigate(item.href),
      });
    });

    config.entities.forEach(entity => {
      if (hasPermission(resolvePermissionForAction('list', entity))) {
        items.push({
          id: `entity:${entity.slug}:list`,
          label: entity.pluralName,
          description: `Open ${entity.pluralName.toLowerCase()} list`,
          group: 'Entities',
          icon: entity.icon ? resolveIcon(entity.icon) : undefined,
          keywords: [entity.name, entity.slug, 'list'],
          onSelect: () => navigate(`/${entity.slug}`),
        });
      }

      if (entity.pages.includes('create') && hasPermission(resolvePermissionForAction('create', entity))) {
        items.push({
          id: `entity:${entity.slug}:create`,
          label: `Create ${entity.name}`,
          description: `Add a new ${entity.name.toLowerCase()}`,
          group: 'Entities',
          icon: entity.icon ? resolveIcon(entity.icon) : undefined,
          keywords: [entity.name, entity.slug, 'create', 'new'],
          onSelect: () => navigate(`/${entity.slug}/new`),
        });
      }
    });

    recentItems.forEach(item => {
      items.push({
        id: `recent:${item.id}`,
        label: item.label,
        description: item.href,
        group: 'Recent',
        keywords: [item.href],
        onSelect: () => navigate(item.href),
      });
    });

    return items;
  }, [flattenedSidebar, config.entities, recentItems, hasPermission, navigate]);

  const isDarkMode = theme.mode === 'dark'
    || (
      theme.mode === 'system'
      && typeof window !== 'undefined'
      && window.matchMedia('(prefers-color-scheme: dark)').matches
    );

  const toggleColorMode = () => {
    setTheme({ mode: isDarkMode ? 'light' : 'dark' });
  };

  const presenceAvatars = useMemo(
    () => presenceUsers.map(user => ({
      src: user.avatarUrl,
      fallback: user.name,
      alt: user.name,
      status: user.status || 'online' as const,
    })),
    [presenceUsers],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <Sidebar
        items={sidebarItems}
        variant="floating"
        collapsible
        hideMobileTrigger
        mobileOpen={mobileSidebarOpen}
        onMobileOpenChange={setMobileSidebarOpen}
        checkPermission={hasPermission}
        footer={sidebarFooter}
        className=""
        header={
          <div className="space-y-1 px-2">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                {config.branding?.logoUrl ? (
                  <img
                    src={config.branding.logoUrl}
                    alt={config.name}
                    className="h-8 w-auto"
                  />
                ) : (
                  <Typography variant="h4" className="truncate text-foreground">
                    {config.branding?.logoText || config.name}
                  </Typography>
                )}
              </div>
              {mobileSidebarOpen && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 md:hidden"
                  onClick={() => setMobileSidebarOpen(false)}
                  aria-label={t('shell.closeMenu')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {config.description && (
              <Typography variant="caption" className="line-clamp-2 text-muted-foreground">
                {config.description.split('\n')[0]}
              </Typography>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6 lg:px-8">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label={t('shell.openNavigationMenu')}
              >
                <Menu className="h-4 w-4" />
              </Button>
              <Typography variant="small" className="truncate text-muted-foreground">
                {currentPathLabel}
              </Typography>
            </div>

            <Inline gap={2}>
              {presenceAvatars.length > 0 && (
                <div className="hidden lg:block" aria-label={t('shell.activeUsers')}>
                  <AvatarGroup
                    avatars={presenceAvatars}
                    max={5}
                    size="sm"
                  />
                </div>
              )}
              {supportedLocales.length > 1 && (
                <label className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
                  {t('shell.locale')}
                  <select
                    value={locale}
                    onChange={event => setLocale(event.target.value)}
                    className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label={t('shell.locale')}
                  >
                    {supportedLocales.map(localeOption => (
                      <option key={localeOption} value={localeOption}>
                        {localeOption.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCommandPaletteOpen(true)}
                aria-label={t('shell.openCommandPalette')}
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">{t('shell.searchButton')}</span>
                <span className="hidden rounded-md border border-border px-1.5 py-0.5 text-xs text-muted-foreground sm:inline">
                  {t('shell.commandShortcut')}
                </span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleColorMode}
                aria-label={t('shell.toggleDarkMode')}
              >
                {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              {isAuthEnabled(config.auth) && isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                  aria-label={t('shell.signOut')}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('shell.signOut')}</span>
                </Button>
              )}
            </Inline>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette
        {...getProps()}
        items={commandItems}
        placeholder={t('shell.commandPalettePlaceholder')}
      />
    </div>
  );
}
